<template>
  <article :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[3, 18]" :mobile-default-grid="[1, 14]">
      <header class="articles-detail__header">
        <JssRichText class="articles-detail__title" :field="detailFields?.articleTitle" tag="h1" />
        <div class="articles-detail__meta">
          <span class="articles-detail__author">
            <Icon name="user" />
            {{ authorLine }}
          </span>
          <button
            class="articles-detail__share"
            type="button"
            aria-label="Share article"
            @click="handleShare"
          >
            <Icon name="share" />
          </button>
        </div>
      </header>

      <JssRichText class="articles-detail__intro" :field="detailFields?.articleSubtitle" />

      <JssRichText class="articles-detail__body" :field="detailFields?.articleBody" tag="div" />

      <aside
        class="articles-detail__cta"
        v-if="detailFields?.ctaTitle?.value || detailFields?.ctaDescription?.value"
      >
        <div class="articles-detail__cta-copy">
          <JssRichText class="articles-detail__cta-title" :field="detailFields?.ctaTitle" />
          <JssRichText class="articles-detail__cta-desc" :field="detailFields?.ctaDescription" />
        </div>
        <SiteButton
          v-if="detailFields?.ctaButton?.value"
          class="articles-detail__cta-button"
          :fields="buttonFields(detailFields?.ctaButton?.value)"
        />
      </aside>

      <nav class="articles-detail__pagination" aria-label="Article pagination">
        <a
          v-if="previousArticle"
          class="articles-detail__pagination-link articles-detail__pagination-link--previous"
          :href="previousArticle.url || '#'"
        >
          <span
            class="articles-detail__pagination-arrow articles-detail__pagination-arrow--previous"
          />
          <span>{{ getArticleTitle(previousArticle) }}</span>
        </a>
        <a
          v-if="nextArticle"
          class="articles-detail__pagination-link articles-detail__pagination-link--next"
          :href="nextArticle.url || '#'"
        >
          <span>{{ getArticleTitle(nextArticle) }}</span>
          <span class="articles-detail__pagination-arrow articles-detail__pagination-arrow--next" />
        </a>
      </nav>
    </ContentGrid>
  </article>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import api from '@/api';
import utils from '@/utils';
import { shareToLinkedIn } from '@/utils/dom-utils';
import type { ArticlesDetailProps } from '@/types/components/ArticlesDetail';

const props = defineProps<ArticlesDetailProps>();

const articleDetail = ref<any>(null);
const relatedArticles = ref<any[]>([]);
const hasComponentFields = computed(() => Object.keys(props.fields || {}).length > 0);

const currentArticle = computed(() => {
  if (hasComponentFields.value) {
    return {
      id: props.rendering?.dataSource,
      fields: props.fields,
      url: getArticleUrl({ fields: props.fields }),
    };
  }
  return articleDetail.value;
});

const detailFields = computed(() => {
  if (hasComponentFields.value) return props.fields || {};
  return articleDetail.value?.fields || articleDetail.value || props.page?.fields || {};
});

const rootClass = computed(() => utils.overall.baseComClass(detailFields.value, 'articles-detail'));
const rootStyle = computed(() => utils.overall.baseComStyle(detailFields.value));

const authorLine = computed(() => {
  const name = detailFields.value?.authorName?.value || '';
  const title = detailFields.value?.authorTitle?.value || '';
  return [name, title].filter(Boolean).join(', ');
});

const currentIndex = computed(() => {
  if (!currentArticle.value) return -1;
  return relatedArticles.value.findIndex((item) => isSameArticle(item, currentArticle.value));
});

const previousArticle = computed(() => {
  if (relatedArticles.value.length === 0) return null;
  const index = currentIndex.value;
  if (index <= 0) return null;
  return relatedArticles.value[index - 1] || null;
});

const nextArticle = computed(() => {
  if (relatedArticles.value.length === 0) return null;
  const index = currentIndex.value;
  if (index < 0) return null;
  return relatedArticles.value[index + 1] || null;
});

const getArticleTitle = (item: any) => {
  return item?.fields?.articleTitle?.value?.replace(/<[^>]+>/g, '') || '';
};

const getShareUrl = () => {
  if (detailFields.value?.shareLink?.value?.href) return detailFields.value.shareLink.value.href;
  if (typeof window === 'undefined') return '';
  return window.location.href;
};

const handleShare = () => {
  const url = getShareUrl();
  shareToLinkedIn(url);
};

const buttonFields = (button: any) => ({
  text: button?.text,
  link: button?.link,
  icon: button?.icon,
  size: { fields: button?.size?.value },
  theme: { fields: button?.theme?.value },
  type: { fields: button?.type?.value },
});

const getCurrentArticleKey = () => {
  if (typeof window === 'undefined') return '';
  return normalizeSlug(window.location.pathname.split('/').filter(Boolean).pop() || '');
};

const normalizeApiData = (res: any, ex: any) => {
  return res || ex || {};
};

const normalizeSlug = (value?: string) => {
  return decodeURIComponent(value || '')
    .trim()
    .replace(/\/+$/, '')
    .toLowerCase();
};

const normalizePath = (value?: string) => {
  const path = (value || '').split('?')[0].split('#')[0];
  return normalizeSlug(path);
};

const getArticleUrl = (item: any) => {
  return item?.url || item?.fields?.articleButton?.value?.link?.value?.href || '';
};

const isSameArticle = (source: any, target: any) => {
  if (!source || !target) return false;
  if (source.id && target.id && source.id === target.id) return true;
  if (source.name && target.name && source.name === target.name) return true;
  const sourceUrl = normalizePath(getArticleUrl(source));
  const targetUrl = normalizePath(getArticleUrl(target));
  return !!sourceUrl && !!targetUrl && sourceUrl === targetUrl;
};

const matchCurrentArticle = (item: any, currentPath: string, currentKey: string) => {
  const itemUrl = normalizePath(getArticleUrl(item));
  const itemName = normalizeSlug(item?.name || item?.displayName);
  const path = normalizePath(currentPath);

  return (
    (!!itemUrl &&
      (itemUrl === path || path.endsWith(itemUrl) || itemUrl.endsWith(`/${currentKey}`))) ||
    (!!itemName && itemName === currentKey)
  );
};

const loadArticles = async () => {
  const [res, ex] = await api.articles.getArticles({ page: 1, size: 10000 });
  const data = normalizeApiData(res, ex);
  relatedArticles.value = data?.list || [];

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const currentKey = getCurrentArticleKey();
  if (!hasComponentFields.value) {
    articleDetail.value =
      relatedArticles.value.find((item) => matchCurrentArticle(item, currentPath, currentKey)) ||
      null;
  }
};
onMounted(async () => {
  await loadArticles();
});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.articles-detail {
  position: relative;
  width: 100%;
  padding: 36px 0 48px;
  background: $white;

  &__header {
    display: grid;
    gap: 18px;
  }

  &__title {
    margin: 0;
    color: #111111;
    font-size: 28px;
    font-weight: 500;
    line-height: 1.18;
  }

  &__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__author {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #656565;
    font-size: 12px;
    line-height: 1.4;

    svg {
      width: 14px;
      height: 14px;
      color: #656565;
      fill: #656565;
    }
  }

  &__share {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    color: #111111;
    background: transparent;
    border: 0;
    cursor: pointer;

    svg {
      width: 28px;
      height: 28px;
    }

    &:hover,
    &:focus-visible {
      color: #739b78;
      outline: none;
    }
  }

  &__intro,
  &__body {
    margin-top: 24px;
    color: #111111;
    font-size: 14px;
    line-height: 1.65;

    p {
      margin: 0 0 18px;
    }

    h2,
    h3,
    h4 {
      margin: 32px 0 14px;
      color: #739b78;
      font-weight: 700;
      line-height: 1.3;
    }

    h2 {
      font-size: 20px;
    }

    h3,
    h4 {
      font-size: 16px;
    }
  }

  &__hero {
    overflow: hidden;
    width: min(100%, 360px);
    margin-top: 24px;
    border-radius: 8px;
    aspect-ratio: 3 / 2;
    background: #eef2ef;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__cta {
    display: grid;
    gap: 18px;
    align-items: center;
    margin-top: 32px;
    padding: 18px;
    border-radius: 8px;
    background: #f1f4f1;
  }

  &__cta-title {
    color: #111111;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
  }

  &__cta-desc {
    margin-top: 8px;
    color: #656565;
    font-size: 13px;
    line-height: 1.45;
  }

  &__pagination {
    display: grid;
    grid-template-columns: 1fr;
    gap: 18px;
    margin-top: 40px;
    padding-top: 18px;
    border-top: 1px solid #e7e7e7;
  }

  &__pagination-link {
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    gap: 8px;
    align-items: center;
    color: #404040;
    font-size: 12px;
    line-height: 1.3;
    text-decoration: none;

    &--next {
      grid-template-columns: minmax(0, 1fr) 18px;
      color: #739b78;
      text-align: right;
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  &__pagination-arrow {
    position: relative;
    width: 14px;
    height: 14px;
    color: currentColor;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 8px;
      height: 8px;
      border-top: 1.5px solid currentColor;
      border-right: 1.5px solid currentColor;
    }

    &--previous::before {
      transform: translate(-35%, -50%) rotate(-135deg);
    }

    &--next::before {
      transform: translate(-65%, -50%) rotate(45deg);
    }
  }

  @include tablet-landscape {
    padding: 56px 0 72px;

    &__title {
      font-size: 32px;
    }

    &__intro,
    &__body {
      margin-top: 28px;
      font-size: 14px;
    }

    &__hero {
      width: 360px;
      margin-top: 28px;
    }

    &__cta {
      grid-template-columns: minmax(0, 1fr) auto;
      padding: 22px 28px;
    }

    &__pagination {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 32px;
    }
  }
}
</style>
